import { AxiosResponse } from 'axios';

import { FetchNotificationsApiResponse } from '@/interfaces';
import { Notification } from '@/interfaces';
import { notificationServiceClient } from '@/services/api';
import { ensureAuthTokens } from '@/store/authStore';

class NotificationService {
    // Fetch DM Threads (Inbox)
    static async getDMThreadSummaries(): Promise<any> {
        try {
            const response = await notificationServiceClient.get('/notification/dm/inbox');
            return response.data;
        } catch (error) {
            console.error('[NotificationService] Failed to fetch DM threads:', error);
            throw error;
        }
    }

    // Fetch DM Thread Messages
    static async getDMThread(thread_key: string, before?: number, limit?: number): Promise<any> {
        try {
            const params = new URLSearchParams({ thread_key });
            if (before) {
                params.append('before', String(before));
            }
            if (limit) {
                params.append('limit', String(limit));
            }

            const response = await notificationServiceClient.get(
                `/notification/dm/thread?${params.toString()}`
            );
            return response.data;
        } catch (error) {
            console.error('[NotificationService] Failed to fetch DM thread:', error);
            throw error;
        }
    }

    static async getHasPushSubscription(): Promise<boolean> {
        if (!ensureAuthTokens()) {
            console.warn('[NotificationService] No access token found');
            return false;
        }

        try {
            const response = await notificationServiceClient.get(
                '/notification/has-push-subscription'
            );
            return response.data?.has_push_subscription === true;
        } catch (error) {
            console.warn('[NotificationService] Failed to check push subscription:', error);
            return false;
        }
    }

    static async send_user_push(saveObj: Record<string, any>): Promise<AxiosResponse> {
        try {
            const response = await notificationServiceClient.post(
                '/notification/send-user-push',
                saveObj
            );
            return response;
        } catch (error) {
            console.error('Error sending feedback:', error);
            throw error;
        }
    }

    static async getNotifications(
        limit: number,
        lastEvaluatedKey: any = null
    ): Promise<FetchNotificationsApiResponse> {
        if (!ensureAuthTokens()) {
            console.warn('[NotificationService] No access token found');
            return {
                notifications: [],
                lastEvaluatedKey: null,
            };
        }

        const serializedKey = lastEvaluatedKey ? JSON.stringify(lastEvaluatedKey) : null;

        try {
            const response = await notificationServiceClient.get<FetchNotificationsApiResponse>(
                '/notification/get',
                {
                    params: {
                        limit,
                        lastEvaluatedKey: serializedKey,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.warn('[NotificationService] Error fetching notifications:', error);
            return {
                notifications: [],
                lastEvaluatedKey: null,
            };
        }
    }

    static async pushSubscribe(subscription: any) {
        if (!ensureAuthTokens()) {
            return Promise.reject('No access token found');
        }
        return notificationServiceClient.post('/notification/push-subscription', subscription);
    }

    static async getUnreadNotificationCount() {
        if (!ensureAuthTokens()) {
            return Promise.reject('No access token found');
        }
        return notificationServiceClient.get('/notification/get-unread-count');
    }

    static async markAsRead(notifications: any[]) {
        if (!ensureAuthTokens()) {
            return Promise.reject('No access token found');
        }
        console.log('markAsRead', notifications);
        return notificationServiceClient.post('/notification/mark-as-read', {
            notifications: notifications,
        });
    }

    static async deleteNotifications(notifications: Notification[]) {
        if (!ensureAuthTokens()) {
            return Promise.reject('No access token found');
        }
        // Ensure notifications contain only PK and SK
        // const notificationsPayload = notifications.map(notification => ({
        //     PK: notification.PK,
        //     SK: notification.SK,
        // }));
        return notificationServiceClient.post(
            '/notification/delete-notifications',
            { notifications: notifications }
            // {
            //     headers: {
            //         Authorization: `Bearer ${access_token}`,
            //     },
            // }
        );
    }
}

export default NotificationService;
